import { Component, Suspense } from "react";
import type { ErrorInfo, ReactNode } from "react";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { QueryError } from "utils/queries";
import "./QueryBoundary.css";

type QueryBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
};

/**
 * Suspense wrapper paired with a query error boundary — the standard boundary
 * for page content fetching via suspense queries (and lazy-loaded chunks).
 *
 * The default fallback centers a spinner within the surrounding pane; pass
 * `fallback` for contexts that need different loading UI (e.g. the full-page
 * spinner at the route root).
 */
export default function QueryBoundary(props: QueryBoundaryProps) {
  const fallback = props.fallback ?? (
    <div className="query-boundary-spinner-container">
      <TalaeraSpinner />
    </div>
  );

  return (
    <Suspense fallback={fallback}>
      <QueryErrorBoundary>{props.children}</QueryErrorBoundary>
    </Suspense>
  );
}

type QueryErrorBoundaryProps = {
  children: ReactNode;
};

type QueryErrorBoundaryState = {
  error: QueryError | null;
};

export class QueryErrorBoundary extends Component<QueryErrorBoundaryProps> {
  state: QueryErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown) {
    if (error instanceof QueryError) {
      return { error };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Error caught in QueryErrorBoundary:", error, info);
  }

  render() {
    if (this.state.error) {
      if (this.state.error instanceof QueryError) {
        return <div>API Error: {this.state.error.message}</div>;
      }
      throw this.state.error;
    }

    return this.props.children;
  }
}
